import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { REQUEST, RESPONSE } from '@nguniversal/express-engine/tokens';
import { Request, Response } from 'express';
import * as Rx from 'rxjs';

export interface LocationEvent {
  hash: string; // a '#' followed by the fragment identifier of the URL.
  hostname: string; // the domain of the URL
  href: string; // the entire URL
  origin: string; // the origin of the specific location (protocol + :// + hostname)
  pathname: string; // an initial '/' followed by the path of the URL
  search: string; // a '?' followed by the parameters or "querystring" of the URL
}

/*
 * Reactive & server-side friendly version of window.location
 */
@Injectable({
  providedIn: 'root',
})
export class Location {
  private routeLoaded = false;
  private locationSubject$ = new Rx.ReplaySubject<LocationEvent>(1);

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional() @Inject(REQUEST) private request: Request,
    @Optional() @Inject(RESPONSE) private response: Response,
  ) {}

  /*
   * Emits a stream of LocationEvent
   */
  valueChanges(): Rx.Observable<LocationEvent> {
    return this.locationSubject$.asObservable();
  }

  /**
   * SSR-friendly version of Router::navigateByUrl()
   */
  navigateByUrl(url: string | UrlTree): Rx.Observable<boolean> {
    if (isPlatformBrowser(this.platformId)) {
      // tslint:disable-next-line:no-navigate-by-url
      return Rx.from(this.router.navigateByUrl(url));
    }
    this.response.redirect(url.toString());
    return Rx.NEVER as Rx.Observable<boolean>;
  }

  /**
   * Sets the location to a provided url. This will leave the webapp, DO NOT
   * use it for internal redirects, use navigateByUrl instead
   */
  redirect(url: string): Rx.Observable<boolean> {
    if (isPlatformBrowser(this.platformId)) {
      if (this.routeLoaded) {
        window.location.assign(url);
      } else {
        window.location.replace(url);
      }
    } else {
      this.response.redirect(url);
    }
    return Rx.NEVER as Rx.Observable<boolean>;
  }

  reload(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.location.reload();
    }
  }

  /*
   * Returns the current origin in a platform friendly way
   */
  get origin(): string {
    if (isPlatformBrowser(this.platformId)) {
      return window.location.origin;
    }
    return `${this.request.protocol}://${this.hostname}`;
  }

  /*
   * Returns the current hostname in a platform friendly way
   */
  get hostname(): string {
    if (isPlatformBrowser(this.platformId)) {
      return window.location.hostname;
    }
    // FIXME: we should be using `req.hostname` here but Express incorrectly
    // strips off the port number, which breaks local dev. This has been fixed
    // in Express 5 -> https://expressjs.com/en/guide/migrating-5.html#req.host
    // Note: X-Forwarded-Host is normally only ever a single value, but
    // Fastly seems to append the values.
    const header =
      this.request.get('X-Forwarded-Host') ||
      (this.request.get('Host') as string);
    const index = header.indexOf(',');
    return index !== -1 ? header.substring(0, index).trim() : header.trim();
  }

  /*
   * Returns the current (absolute) URL in a platform friendly way
   */
  get href(): string {
    if (isPlatformBrowser(this.platformId)) {
      return window.location.href;
    }
    return `${this.origin}${this.request.originalUrl}`;
  }

  /*
   * Returns the current URL in a platform friendly way
   */
  get url(): string {
    if (isPlatformBrowser(this.platformId)) {
      return `${window.location.pathname}${window.location.search}`;
    }
    return this.request.originalUrl;
  }

  /**
   * [PRIVATE] Exported private API.
   * To be used only by the LocationComponent to push new locations
   */
  _setLocation(pathname: string): void {
    this.locationSubject$.next({
      hash: '', // TODO
      hostname: this.hostname,
      href: `${this.origin}${pathname}`,
      origin: this.origin,
      pathname,
      search: '', // TODO
    });
  }

  /**
   * [PRIVATE] exported private API
   * To be used only by the LocationComponent to mark that routes are loaded
   */
  _routeHasLoaded() {
    this.routeLoaded = true;
  }
}
